import { DATE_FORMATS, DateUtils } from "./dateUtils";

type StableSortParams<T, U extends string> = {
  items: T[];
  orderBy: U;
  order: "asc" | "desc";
  getValueByKey: (item: T, orderBy: U) => unknown;
};

type FilterRowsParams<T, U extends string> = {
  items: T[];
} & (
  | {
      criteria?: Partial<Record<U, unknown>>;
      getValueByCriteriaKey: (item: T, criteriaKey: U) => unknown;
      predicate?: never;
    }
  | {
      criteria?: never;
      getValueByCriteriaKey?: never;
      predicate: (item: T) => boolean;
    }
);

class ArrayUtils {
  /**
   * Splits an array into multiple chunks of a specified size.
   *
   * @template T - The type of elements in the input array.
   * @param array - The array to be split into chunks.
   * @param chunkSize - The maximum size of each chunk. Must be greater than 0.
   * @returns An array of arrays (chunks), where each chunk contains up to `chunkSize` elements
   * from the original array.
   *
   * @example
   * ```typescript
   * const arr = [1, 2, 3, 4, 5];
   * const chunks = splitArrayInChunks(arr, 2);
   * // chunks = [[1, 2], [3, 4], [5]]
   * ```
   */
  splitArrayInChunks<T>(array: T[], chunkSize: number): T[][] {
    if (chunkSize <= 0) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[splitArrayInChunks] chunkSize of 0 is invalid. Returning empty array."
        );
      }

      return [];
    }

    const resultArray: T[][] = [];

    array.forEach((item, index) => {
      const chunkIndex = Math.floor(index / chunkSize);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = [];
      }

      resultArray[chunkIndex].push(item);
    });

    return resultArray;
  }

  /**
   * Performs a stable sort on an array of items based on a specified key and order.
   *
   * The sorting algorithm maintains the relative order of items with equal keys (stable sort).
   * Supports sorting by string or number values, with locale-aware string comparison and
   * handling of nullish and NaN values.
   *
   * @template T - The type of the items in the array.
   * @template U - The type of the key used for sorting (extends string) - same type as the orderBy parameter.
   *
   * @param params - The parameters for sorting.
   * @param params.getValueByKey - A function that retrieves the value to sort by from an item.
   * @param params.items - The array of items to be sorted.
   * @param params.order - The sort order: "asc" for ascending or "desc" for descending.
   * @param params.orderBy - The key to sort the items by.
   *
   * @returns A new array containing the sorted items.
   *
   * @remarks
   * - Nullish values (null or undefined) are always sorted to the end.
   * - Items with equal sort values retain their original order.
   * - If the sort value is neither a string nor a number, a warning is logged.
   * - For string values, locale-aware comparison is used with numeric sorting enabled.
   */
  stableSort<T, U extends string>({
    getValueByKey,
    items,
    order,
    orderBy,
  }: StableSortParams<T, U>): T[] {
    // Input validation
    if (!Array.isArray(items)) {
      return [];
    }

    if (items.length <= 1) {
      return [...items]; // Return copy for consistency
    }

    const stableArray = items.map((el, index) => [el, index] as const);

    stableArray.sort((a, b) => {
      const aValue = getValueByKey(a[0], orderBy);
      const bValue = getValueByKey(b[0], orderBy);

      const aIsNullish = aValue === null || aValue === undefined;
      const bIsNullish = bValue === null || bValue === undefined;

      if (aIsNullish && !bIsNullish) return 1;
      if (!aIsNullish && bIsNullish) return -1;
      if (aIsNullish && bIsNullish) return a[1] - b[1]; // Maintain stable order

      // Numeric comparison
      if (typeof aValue === "number" && typeof bValue === "number") {
        if (isNaN(aValue) && !isNaN(bValue)) return 1;
        if (!isNaN(aValue) && isNaN(bValue)) return -1;
        if (isNaN(aValue) && isNaN(bValue)) return a[1] - b[1];

        const result = aValue - bValue;
        return order === "asc" ? result : -result;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        const aIsDateLike = DateUtils.isDateLike(aValue);
        const bIsDateLike = DateUtils.isDateLike(bValue);

        if (aIsDateLike && bIsDateLike) {
          const aValueTimestamp = DateUtils.parse(
            aValue,
            DATE_FORMATS["DD/MM/YYYY"]
          ).valueOf();

          const bValueTimestamp = DateUtils.parse(
            bValue,
            DATE_FORMATS["DD/MM/YYYY"]
          ).valueOf();

          if (!isNaN(aValueTimestamp) || !isNaN(bValueTimestamp)) {
            if (isNaN(aValueTimestamp) && !isNaN(bValueTimestamp)) return 1;
            if (!isNaN(aValueTimestamp) && isNaN(bValueTimestamp)) return -1;
            if (isNaN(aValueTimestamp) && isNaN(bValueTimestamp)) {
              // fallthrough to string comparison
            } else {
              const diff = aValueTimestamp - bValueTimestamp;
              return order === "asc" ? diff : -diff;
            }
          }
        }
      }
      // String comparison with locale support
      const aString = String(aValue);
      const bString = String(bValue);

      const compareResult = aString.localeCompare(bString, undefined, {
        sensitivity: "case",
        numeric: true,
      });

      if (compareResult !== 0) {
        return order === "asc" ? compareResult : -compareResult;
      }

      // Boolean comparison
      if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        return order === "asc"
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue);
      }

      // Maintain stable order for equal values
      return a[1] - b[1];
    });

    return stableArray.map((el) => el[0]);
  }

  /**
   * Filters an array of items based on a set of criteria.
   *
   * @template T - The type of items in the array.
   * @template U - The keys of the criteria object, constrained to strings.
   * @param params - The parameters for filtering.
   * @param params.criteria - An object where each key corresponds to a property to filter by,
   * and each value is the value to match.
   * @param params.getValueByCriteriaKey - A function that retrieves the value of an item
   * by a given criteria key.
   * @param params.items - The array of items to filter.
   * @returns A new array containing only the items that match all specified criteria.
   *
   * @remarks
   * - If `criteria` is empty or undefined, all items are returned.
   * - Criteria values of `undefined` are ignored.
   * - Criteria values of `null` require the item's value to also be `null`.
   * - String criteria perform a case-insensitive substring match.
   * - Number criteria require strict equality.
   * - Other types fall back to strict equality.
   */
  filterItems<T, U extends string>({
    criteria,
    getValueByCriteriaKey,
    items,
    predicate,
  }: FilterRowsParams<T, U>): T[] {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    if (typeof predicate === "function") {
      return items.filter(predicate);
    }

    if (criteria && Object.keys(criteria).length > 0) {
      return items.filter((item) =>
        Object.entries(criteria).every(([key, filterValue]) => {
          const itemValue = getValueByCriteriaKey(item, key as U);

          // Ignore undefined criteria
          if (filterValue === undefined) {
            return true;
          }

          if (filterValue === null) {
            return itemValue === null;
          }

          if (
            typeof filterValue === "string" &&
            typeof itemValue === "string"
          ) {
            return itemValue.toLowerCase().includes(filterValue.toLowerCase());
          }

          // Fallback to strict equality for other types
          return itemValue === filterValue;
        })
      );
    }

    return items;
  }

  /**
   * Returns a paginated subset of items based on the current page and rows per page.
   *
   * @template T The type of items in the array.
   * @param params - An object containing pagination parameters.
   * @param params.currentPage - The zero-based index of the current page.
   * @param params.items - The array of items to paginate.
   * @param params.rowsPerPage - The number of items per page.
   * @returns An array containing the items for the specified page.
   * Returns an empty array if the input is invalid or if rowsPerPage is less than or equal to zero.
   *
   * @remarks
   * If `rowsPerPage` is less than or equal to zero,
   * a warning is logged in non-production environments and an empty array is returned.
   */
  paginateItems<T>({
    currentPage,
    items,
    rowsPerPage,
  }: {
    currentPage: number;
    items: T[];
    rowsPerPage: number;
  }): T[] {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    if (rowsPerPage <= 0) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[paginateItems] rowsPerPage of 0 is invalid. Returning empty array."
        );
      }

      return [];
    }

    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return items.slice(startIndex, endIndex);
  }
}

const arrayUtils = new ArrayUtils();
export default arrayUtils;
